// MessengerBubble — the Intercom-style floating chat widget rendered on
// every authenticated page (from app/layout.tsx). Collapsed = a FAB
// with an unread-count badge; expanded = a 360×500 panel with a
// conversation list and, on click, a thread view (transcript +
// subscriber-context strip + claim/take-over controls + a reply box
// for Messenger / a "reply via Page Inbox" notice for WhatsApp).
//
// Visible to admin + support (gated on `messenger:reply`). Live via
// `events:messenger`; refetches every 60s as a safety net. The React
// port of layout.php's render_messenger_bubble() + the
// /v1/frag/messenger/{bubble,list,conversation} fragments.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  Hand,
  MessageCircle,
  MessageSquareText,
  Send,
  Settings2,
  UserCheck,
  UserPlus,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
  cn,
  toast,
} from "@noc/ui";
import type { MessengerConversation, MessengerConversationView, MessengerMessage } from "@noc/api";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useCan } from "@/features/auth/roles";
import { useEventStream } from "@/lib/sse";

export function MessengerBubble() {
  const canReply = useCan("messenger:reply");
  if (!canReply) return null;
  return <MessengerBubbleInner />;
}

function MessengerBubbleInner() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const isAdmin = useCan("admin:config");

  // Live: refetch the badge/list + the open thread on any messenger event;
  // pop a toast for inbound messages while the panel is closed.
  useEventStream(["messenger"], (ev) => {
    void qc.invalidateQueries({ queryKey: qk.messenger.all() });
    const d = (ev.data ?? {}) as { type?: string; sender?: string; text?: string };
    if (!open && d.type === "inbound") {
      toast.message(`New message${d.sender ? ` from ${d.sender}` : ""}`, { description: d.text || undefined });
    }
  });

  const bubbleQ = useQuery({
    queryKey: qk.messenger.bubble(),
    queryFn: async () => {
      const r = await api.messenger.bubble();
      if (!r.ok) throw new Error(r.error ?? "messenger inbox unavailable");
      return r;
    },
    refetchInterval: 60_000,
    retry: false,
  });
  const unread = bubbleQ.data?.unread_total ?? 0;
  const viewerUID = bubbleQ.data?.viewer_uid ?? 0;

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 print:hidden">
      {open && (
        <Card className="flex h-[500px] max-h-[calc(100vh-5rem)] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden shadow-xl">
          {/* header */}
          <div className="flex items-center gap-2 border-b bg-card px-3 py-2">
            {selected != null && (
              <Button variant="ghost" size="icon" className="size-7" onClick={() => setSelected(null)} title="Back to list">
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <MessageCircle className="size-4 text-primary" />
            <span className="text-sm font-semibold">Social Inbox</span>
            {unread > 0 && selected == null && <Badge variant="default">{unread}</Badge>}
            <div className="ml-auto flex items-center gap-1">
              {isAdmin && (
                <Button asChild variant="ghost" size="icon" className="size-7" title="Provider settings">
                  <Link to="/messenger">
                    <Settings2 className="size-3.5" />
                  </Link>
                </Button>
              )}
              <Button variant="ghost" size="icon" className="size-7" onClick={() => setOpen(false)} title="Close">
                <X className="size-4" />
              </Button>
            </div>
          </div>
          {/* body */}
          {selected == null ? (
            <ConversationList
              loading={bubbleQ.isPending}
              error={bubbleQ.isError ? (bubbleQ.error instanceof Error ? bubbleQ.error.message : "error") : null}
              recent={bubbleQ.data?.recent ?? []}
              onOpen={(id) => {
                setSelected(id);
                void api.messenger.markRead(id).then(() => qc.invalidateQueries({ queryKey: qk.messenger.all() }));
              }}
            />
          ) : (
            <Thread convId={selected} viewerUID={viewerUID} onChanged={() => qc.invalidateQueries({ queryKey: qk.messenger.all() })} />
          )}
        </Card>
      )}

      {/* collapsed FAB */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        title={open ? "Close inbox" : "Open inbox"}
      >
        {open ? <X className="size-5" /> : <MessageCircle className="size-5" />}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-semibold text-destructive-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    </div>
  );
}

// ── list view ─────────────────────────────────────────────────────────

function ConversationList({
  loading,
  error,
  recent,
  onOpen,
}: {
  loading: boolean;
  error: string | null;
  recent: MessengerConversationView[];
  onOpen: (id: number) => void;
}) {
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center text-sm text-muted-foreground">
        <p>Inbox unavailable.</p>
        <p className="text-xs">{error}</p>
      </div>
    );
  }
  if (recent.length === 0) {
    return <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">No conversations yet.</div>;
  }
  return (
    <ul className="flex-1 divide-y overflow-y-auto">
      {recent.map((v) => (
        <li key={v.conv.id}>
          <button
            type="button"
            onClick={() => onOpen(v.conv.id)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent"
          >
            <Avatar name={v.conv.display_name} url={v.conv.profile_pic_url} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium">{v.conv.display_name || "(unknown)"}</span>
                <ChannelBadge channel={v.conv.channel} label={v.channel_label} />
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {v.rel_time}
                {v.assignee_username ? ` · ${v.assignee_username}` : ""}
                {v.conv.subscriber_username ? ` · ${v.conv.subscriber_username}` : ""}
              </div>
            </div>
            {v.conv.unread_count > 0 && <Badge variant="default">{v.conv.unread_count}</Badge>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Avatar({ name, url }: { name: string; url: string }) {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return <img src={url} alt="" className="size-8 shrink-0 rounded-full object-cover" onError={() => setBroken(true)} />;
  }
  return (
    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
      {(name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

function ChannelBadge({ channel, label }: { channel: string; label: string }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded px-1 py-px text-[9px] font-medium uppercase tracking-wide",
        channel === "whatsapp" ? "bg-success/15 text-success" : "bg-primary/15 text-primary",
      )}
    >
      {label || channel}
    </span>
  );
}

// ── thread view ───────────────────────────────────────────────────────

function Thread({ convId, viewerUID, onChanged }: { convId: number; viewerUID: number; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.messenger.conversation(convId),
    queryFn: async () => {
      const r = await api.messenger.conversation(convId);
      if (!r.ok) throw new Error(r.error ?? "conversation unavailable");
      return r;
    },
    refetchInterval: 30_000,
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [q.data?.messages.length]);

  const claimM = useMutation({
    mutationFn: () => api.messenger.claim(convId),
    onSuccess: (r) => {
      if (r.ok && r.claimed) toast.success("Claimed.");
      else if (r.ok) toast.warning(`Already claimed by ${r.assigned_username ?? "someone"}.`);
      else toast.error(r.error ?? "claim failed");
      onChanged();
      void qc.invalidateQueries({ queryKey: qk.messenger.conversation(convId) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "claim failed"),
  });
  const takeOverM = useMutation({
    mutationFn: () => api.messenger.takeOver(convId),
    onSuccess: () => {
      toast.success("Taken over.");
      onChanged();
      void qc.invalidateQueries({ queryKey: qk.messenger.conversation(convId) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "take-over failed"),
  });
  const releaseM = useMutation({
    mutationFn: () => api.messenger.release(convId),
    onSuccess: () => {
      toast.success("Released.");
      onChanged();
      void qc.invalidateQueries({ queryKey: qk.messenger.conversation(convId) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "release failed"),
  });

  if (q.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
        {q.error instanceof Error ? q.error.message : "error"}
      </div>
    );
  }
  const { view, messages, outbound_supported } = q.data;
  const conv = view.conv;
  const assignedToMe = conv.assigned_uid === viewerUID && viewerUID !== 0;

  return (
    <>
      {/* thread header */}
      <div className="border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <Avatar name={conv.display_name} url={conv.profile_pic_url} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-semibold">{conv.display_name || "(unknown)"}</span>
              <ChannelBadge channel={conv.channel} label={view.channel_label} />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {view.phone_pretty || conv.external_id}
            </div>
          </div>
        </div>
        {/* subscriber-context strip */}
        <SubscriberStrip conv={conv} convId={convId} onChanged={() => qc.invalidateQueries({ queryKey: qk.messenger.all() })} />
        {/* assignment row */}
        <div className="mt-1.5 flex items-center gap-1.5 text-xs">
          {conv.assigned_uid === 0 ? (
            <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={() => claimM.mutate()} disabled={claimM.isPending}>
              {claimM.isPending ? <Spinner /> : <Hand className="size-3" />} Claim
            </Button>
          ) : assignedToMe ? (
            <>
              <Badge variant="success">you</Badge>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-muted-foreground" onClick={() => releaseM.mutate()} disabled={releaseM.isPending}>
                Release
              </Button>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Assigned to <span className="font-medium text-foreground">{view.assignee_username || `uid ${conv.assigned_uid}`}</span></span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => takeOverM.mutate()} disabled={takeOverM.isPending}>
                Take over
              </Button>
            </>
          )}
        </div>
      </div>

      {/* transcript */}
      <div ref={scrollRef} className="flex flex-1 flex-col gap-2 overflow-y-auto bg-muted/20 p-3">
        {messages.length === 0 ? (
          <p className="m-auto text-sm text-muted-foreground">No messages.</p>
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} />)
        )}
      </div>

      {/* compose / notice */}
      {outbound_supported ? (
        <Compose convId={convId} onSent={() => qc.invalidateQueries({ queryKey: qk.messenger.conversation(convId) })} />
      ) : (
        <div className="border-t bg-muted/30 px-3 py-2 text-center text-xs text-muted-foreground">
          Reply from the FB Page&nbsp;Inbox app — those replies appear here automatically.
        </div>
      )}
    </>
  );
}

// SubscriberStrip renders the green "Subscriber: …" chip when the
// conversation is linked, and offers a "Link to subscriber" button
// when it isn't (or a small "Change" / "Unlink" pair when it IS).
// Operator search-and-pick from the existing /v1/api/suggest endpoint
// so the same typeahead UX is used everywhere.
//
// Manual linking is the only path to a real customer name now that
// Meta's User Profile API is gated to Tech Providers — see
// project_messenger_integration memory.
function SubscriberStrip({
  conv,
  convId,
  onChanged,
}: {
  conv: MessengerConversation;
  convId: number;
  onChanged: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const unlinkM = useMutation({
    mutationFn: () => api.messenger.linkSubscriber(convId, ""),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success("Subscriber unlinked");
        onChanged();
      } else toast.error(r.error ?? "unlink failed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "unlink failed"),
  });

  return (
    <>
      {conv.subscriber_username ? (
        <div className="mt-1.5 flex items-center gap-1 rounded bg-primary/10 px-2 py-1 text-xs text-primary">
          <Link
            to={`/subscribers/${encodeURIComponent(conv.subscriber_username)}`}
            className="flex flex-1 items-center gap-1 hover:underline"
          >
            <UserCheck className="size-3.5" />
            Subscriber: <span className="font-mono font-medium">{conv.subscriber_username}</span>
            <ExternalLink className="ml-1 size-3" />
          </Link>
          <button
            type="button"
            className="rounded px-1 text-[10px] hover:bg-primary/20"
            onClick={() => setPickerOpen(true)}
            title="Change linked subscriber"
          >
            Change
          </button>
          <button
            type="button"
            className="rounded px-1 text-[10px] hover:bg-primary/20"
            onClick={() => {
              if (window.confirm("Unlink subscriber from this conversation?")) unlinkM.mutate();
            }}
            disabled={unlinkM.isPending}
            title="Unlink"
          >
            Unlink
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="mt-1.5 flex w-full items-center gap-1 rounded border border-dashed border-input bg-muted/30 px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50"
          onClick={() => setPickerOpen(true)}
          title="Link this conversation to a subscriber record"
        >
          <UserPlus className="size-3.5" /> Link to subscriber…
        </button>
      )}
      <SubscriberLinkDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        convId={convId}
        onLinked={() => {
          setPickerOpen(false);
          onChanged();
        }}
      />
    </>
  );
}

function SubscriberLinkDialog({
  open,
  onClose,
  convId,
  onLinked,
}: {
  open: boolean;
  onClose: () => void;
  convId: number;
  onLinked: () => void;
}) {
  const [q, setQ] = useState("");
  const debounced = useDebouncedSubscriberQuery(q.trim(), 200);
  const sq = useQuery({
    queryKey: ["messenger", "subscriberLink", debounced],
    queryFn: async () => {
      if (debounced.length < 2) return [];
      const r = await api.search.suggest(debounced);
      if (!r.ok) throw new Error(r.error ?? "search failed");
      return r.matches ?? [];
    },
    enabled: open,
  });
  const linkM = useMutation({
    mutationFn: (username: string) => api.messenger.linkSubscriber(convId, username),
    onSuccess: (r, username) => {
      if (r.ok) {
        toast.success(`Linked to ${username}`);
        onLinked();
      } else toast.error(r.error ?? "link failed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "link failed"),
  });

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link conversation to subscriber</DialogTitle>
          <DialogDescription>
            Search by username, name, mobile, or address. Click a row to attach it to this conversation —
            the subscriber's RADIUS context (status, package, expires_on) will then show in the bubble header.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search…"
            autoFocus
            autoComplete="off"
          />
          {sq.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner /> Searching…
            </div>
          )}
          <div className="max-h-[40vh] overflow-y-auto rounded-md border">
            {(sq.data ?? []).length === 0 && q.trim().length >= 2 && !sq.isLoading ? (
              <p className="p-3 text-center text-xs text-muted-foreground">No subscribers match.</p>
            ) : (
              (sq.data ?? []).map((m) => (
                <button
                  key={m.username}
                  type="button"
                  className="block w-full border-b px-3 py-1.5 text-left text-sm last:border-0 hover:bg-accent/50 disabled:opacity-60"
                  onClick={() => linkM.mutate(m.username)}
                  disabled={linkM.isPending}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{m.username}</span>
                    {m.mobile && <span className="font-mono text-xs">{m.mobile}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.address || m.switch || "—"}
                    {m.status && <> · <span className={m.status === "active" ? "text-success" : "text-warning"}>{m.status}</span></>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useDebouncedSubscriberQuery(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Bubble({ m }: { m: MessengerMessage }) {
  if (m.sender_kind === "system") {
    return <div className="self-center text-center text-[11px] text-muted-foreground">{m.text}</div>;
  }
  const out = !m.inbound;
  const attachments = parseAttachments(m.attachments);
  const hasText = m.text.trim() !== "";
  return (
    <div className={cn("flex max-w-[85%] flex-col gap-0.5", out ? "self-end items-end" : "self-start items-start")}>
      {attachments.length > 0 && (
        <div className="flex flex-col gap-1">
          {attachments.map((att, i) => (
            <AttachmentRender key={i} att={att} />
          ))}
        </div>
      )}
      {(hasText || attachments.length === 0) && (
        <div
          className={cn(
            "whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-sm",
            !out
              ? "rounded-bl-sm bg-card text-card-foreground shadow-sm"
              : m.echo
                ? "rounded-br-sm border border-primary/30 bg-primary/10 text-foreground"
                : "rounded-br-sm bg-primary text-primary-foreground",
          )}
        >
          {m.text || <span className="italic opacity-70">(unsupported message)</span>}
        </div>
      )}
      <div className="px-1 text-[10px] text-muted-foreground" title={m.title}>
        {m.time}
        {m.echo ? " · via Page Inbox" : m.sender_username ? ` · ${m.sender_username}` : ""}
      </div>
    </div>
  );
}

// Meta webhook attachment shape — one row per attachment. Payload
// fields vary by type: images/videos/audio use `url`; stickers add
// `sticker_id`; files add `name`; location adds coordinates.
interface MetaAttachment {
  type: "image" | "video" | "audio" | "file" | "location" | "fallback" | string;
  payload: { url?: string; sticker_id?: number; name?: string } | null;
}

function parseAttachments(raw: string): MetaAttachment[] {
  if (!raw || raw === "null" || raw === "[]") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function AttachmentRender({ att }: { att: MetaAttachment }) {
  const url = att.payload?.url;
  if (!url && att.type !== "location") {
    return <div className="rounded bg-muted px-2 py-1 text-xs italic text-muted-foreground">[{att.type}]</div>;
  }
  switch (att.type) {
    case "image":
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block max-w-[240px]">
          <img
            src={url}
            alt=""
            loading="lazy"
            className="max-h-64 max-w-full rounded-lg object-cover"
            onError={(e) => {
              // Meta CDN URLs sometimes 403 after expiry (~24h). Show fallback.
              (e.currentTarget as HTMLImageElement).style.display = "none";
              const next = (e.currentTarget as HTMLImageElement).nextElementSibling as HTMLElement | null;
              if (next) next.style.display = "";
            }}
          />
          <span className="hidden rounded bg-muted px-2 py-1 text-xs italic text-muted-foreground">
            [image — link expired, click to open]
          </span>
        </a>
      );
    case "video":
      return (
        <video controls preload="metadata" className="max-h-64 max-w-[240px] rounded-lg">
          <source src={url} />
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs underline">
            Download video
          </a>
        </video>
      );
    case "audio":
      return (
        <audio controls preload="metadata" className="max-w-full">
          <source src={url} />
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs underline">
            Download audio
          </a>
        </audio>
      );
    case "file":
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs underline"
        >
          📎 {att.payload?.name || "file"}
        </a>
      );
    default:
      return (
        <div className="rounded bg-muted px-2 py-1 text-xs italic text-muted-foreground">
          [{att.type}]
        </div>
      );
  }
}

function Compose({ convId, onSent }: { convId: number; onSent: () => void }) {
  const [text, setText] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const m = useMutation({
    mutationFn: () => api.messenger.send(convId, text.trim()),
    onSuccess: (r) => {
      if (r.ok) {
        setText("");
        onSent();
      } else if (r.error === "whatsapp_outbound_unsupported") {
        toast.warning("This is a WhatsApp conversation — reply from the FB Page Inbox app.");
      } else {
        toast.error(r.error ?? "send failed");
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "send failed"),
  });

  // Canned-response dropdown. The list is invalidated on `templates_updated`
  // SSE events so a save in the admin page reaches every connected bubble
  // without a refresh.
  const qc = useQueryClient();
  useEventStream(["messenger"], (ev) => {
    const d = (ev.data ?? {}) as { type?: string };
    if (d.type === "templates_updated") {
      void qc.invalidateQueries({ queryKey: qk.messenger.templates() });
    }
  });
  const templatesQ = useQuery({
    queryKey: qk.messenger.templates(),
    queryFn: async () => {
      const r = await api.messenger.listTemplates();
      if (!r.ok) throw new Error(r.error ?? "templates unavailable");
      return r.templates ?? [];
    },
    staleTime: 60_000,
  });
  const templates = templatesQ.data ?? [];

  function insertTemplate(body: string) {
    setText((prev) => (prev.trim() === "" ? body : prev.trimEnd() + " " + body));
    setPickerOpen(false);
  }

  return (
    <form
      className="flex flex-col gap-1 border-t bg-card px-2 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim() && !m.isPending) m.mutate();
      }}
    >
      {pickerOpen && (
        <div className="max-h-48 overflow-y-auto rounded-md border bg-popover text-sm shadow-md">
          {templates.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No canned responses. Add some in /messenger.
            </p>
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className="block w-full px-2 py-1.5 text-left hover:bg-accent"
                onClick={() => insertTemplate(t.body)}
              >
                <div className="truncate text-xs font-medium">{t.label}</div>
                <div className="truncate text-[10px] text-muted-foreground">{t.body}</div>
              </button>
            ))
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0"
          title="Canned responses"
          onClick={() => setPickerOpen((v) => !v)}
        >
          <MessageSquareText className="size-4" />
        </Button>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a reply…"
          className="h-9 flex-1"
          autoComplete="off"
        />
        <Button type="submit" size="icon" className="size-9 shrink-0" disabled={!text.trim() || m.isPending} title="Send">
          {m.isPending ? <Spinner /> : <Send className="size-4" />}
        </Button>
      </div>
    </form>
  );
}
